import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { images } from '../../assets/image';
import { useLanguage } from '../../context/LanguageContext';
import LanguageSelector from '../../components/LanguageSelector';

export default function Login() {
    const [showPassword, setShowPassword] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const { t } = useLanguage();

    const handleLogin = (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        // Simple mock login logic
        setTimeout(() => {
            const emailLower = email.toLowerCase();
            if (emailLower.includes('admin')) {
                navigate('/admin/dashboard');
            } else if (emailLower.includes('teacher')) {
                navigate('/teacher/dashboard');
            } else if (emailLower.includes('accountant')) {
                navigate('/accountant/dashboard');
            } else {
                setError(t('loginError'));
            }
            setLoading(false);
        }, 1000);
    };

    const quickLogin = (role) => {
        setEmail(`${role}@schoolos.com`);
        setPassword('password123');

        setLoading(true);
        setTimeout(() => {
            navigate(`/${role}/dashboard`);
            setLoading(false);
        }, 800);
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 font-sans p-4">
            {/* Language selector at top right */}
            <div className="fixed top-4 right-4 z-50">
                <LanguageSelector variant="compact" />
            </div>

            <div className="w-full max-w-md bg-white p-8 sm:p-10 rounded-2xl shadow-sm border border-gray-100">
                <div className="text-center mb-8">
                    <span className="text-[30px] font-black tracking-tighter text-[#111827]">School-<span className='text-blue-500'>OS</span></span>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('welcomeBack')}</h1>
                    <p className="text-sm text-gray-500">{t('loginSubtitle')}</p>
                </div>

                <form className="flex flex-col gap-5 text-left" onSubmit={handleLogin}>
                    {error && (
                        <div className="p-3 bg-red-50 border border-red-100 rounded-lg text-red-600 text-xs font-semibold animate-shake">
                            {error}
                        </div>
                    )}

                    <div className="flex flex-col gap-1.5">
                        <label className="text-sm font-medium text-gray-700">{t('email')}</label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className="w-full h-11 px-4 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all placeholder-gray-400"
                            placeholder="e.g. admin@schoolos.com"
                        />
                    </div>

                    <div className="flex flex-col gap-1.5 relative">
                        <div className="flex justify-between items-center">
                            <label className="text-sm font-medium text-gray-700">{t('password')}</label>
                            <Link to="/forgot" className="text-xs font-semibold text-blue-500 hover:underline">
                                {t('forgotPassword')}
                            </Link>
                        </div>
                        <div className="relative">
                            <input
                                type={showPassword ? "text" : "password"}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                className="w-full h-11 pl-4 pr-10 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all placeholder-gray-400"
                                placeholder={t('password')}
                            />
                            <button
                                type="button"
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? (
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                                ) : (
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                                )}
                            </button>
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="mt-2 w-full h-11 bg-blue-500 hover:bg-blue-600 active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold transition-all shadow-md shadow-gray-200 flex items-center justify-center gap-2"
                    >
                        {loading ? (
                            <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        ) : t('logIn')}
                    </button>
                </form>

                <div className="mt-8">
                    <div className="relative mb-6">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-100"></div>
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                            <span className="bg-white px-2 text-gray-400 font-medium tracking-wider">{t('quickAccess')}</span>
                        </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                        <button
                            onClick={() => quickLogin('admin')}
                            className="flex flex-col items-center gap-2 p-3 rounded-xl border border-gray-100 hover:border-blue-500 hover:bg-blue-50/50 transition-all group"
                        >
                            <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-500 flex items-center justify-center group-hover:bg-blue-500 group-hover:text-white transition-colors">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>
                            </div>
                            <span className="text-[10px] font-black text-gray-700 uppercase">{t('admin')}</span>
                        </button>
                        <button
                            onClick={() => quickLogin('teacher')}
                            className="flex flex-col items-center gap-2 p-3 rounded-xl border border-gray-100 hover:border-emerald-500 hover:bg-emerald-50/50 transition-all group"
                        >
                            <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-500 flex items-center justify-center group-hover:bg-emerald-500 group-hover:text-white transition-colors">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                            </div>
                            <span className="text-[10px] font-black text-gray-700 uppercase">{t('teacher')}</span>
                        </button>
                        <button
                            onClick={() => quickLogin('accountant')}
                            className="flex flex-col items-center gap-2 p-3 rounded-xl border border-gray-100 hover:border-indigo-500 hover:bg-indigo-50/50 transition-all group"
                        >
                            <div className="w-9 h-9 rounded-lg bg-indigo-50 text-indigo-500 flex items-center justify-center group-hover:bg-indigo-500 group-hover:text-white transition-colors">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"></line><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></svg>
                            </div>
                            <span className="text-[10px] font-black text-gray-700 uppercase">{t('accounts')}</span>
                        </button>
                    </div>
                </div>


                <div className="mt-8 text-center">
                    <p className="text-xs text-gray-500">
                        {t('termsText')} <a href="https://www.nerdos.in/terms" className="text-gray-700 hover:text-gray-900 underline underline-offset-2">{t('terms')}</a> {t('and')} <a href="https://www.nerdos.in/privacy" className="text-gray-700 hover:text-gray-900 underline underline-offset-2">{t('privacyPolicy')}</a>
                    </p>
                </div>
            </div>
        </div>
    );
}